import { useState, useEffect, useRef, type RefObject } from "react";
import PlaygroundTranscript from "@/components/PlaygroundTranscript";
import type { Item } from "@/components/types";
import handleRealtimeEvent from "@/components/handle-realtime-event";
import { base64ToFloat32Array } from "@/components/audio-utils";
import PlaygroundPanel from "@/components/PlaygroundPanel";
import { useNavigate } from "react-router-dom";
import { useCompaniesQuery } from "@/services/companyQueries";
import { usePlaygroundStore } from "@/stores/playgroundStore";
import { useAudioDevices } from "@/hooks/useAudioDevices";
import { useAuth } from "@/hooks/useAuth";

export function PlaygroundPage() {
  const [wsConnected, setWsConnected] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [micActive, setMicActive] = useState(false);

  // Audio device management with improved hook
  const {
    audioInputDevices,
    isLoading: audioDevicesLoading,
    error: audioDevicesError,
    hasPermission: hasAudioPermission,
    refreshDevices,
    requestPermission,
  } = useAudioDevices({
    requestPermissionOnMount: false, // Don't auto-request permission
    enableDeviceChangeDetection: true,
    debounceMs: 300,
  });

  const workletsLoaded = usePlaygroundStore((s) => s.workletsLoaded);
  const setWorkletsLoaded = usePlaygroundStore((s) => s.setWorkletsLoaded);
  const selectedCompanyId = usePlaygroundStore((s) => s.selectedCompanyId);
  const setSelectedCompanyId = usePlaygroundStore(
    (s) => s.setSelectedCompanyId
  );
  const selectedAudioInput = usePlaygroundStore((s) => s.selectedAudioInput);
  const setSelectedAudioInput = usePlaygroundStore(
    (s) => s.setSelectedAudioInput
  );
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);
  const outputProcessorNodeRef = useRef<AudioWorkletNode | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const { data: companies = [], isLoading: companiesLoading } =
    useCompaniesQuery();
  const { localUser } = useAuth();

  // Remove old device enumeration logic - now handled by useAudioDevices hook

  // Ensure default company selection once companies are loaded and no selection exists
  useEffect(() => {
    if (!selectedCompanyId && companies.length > 0) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  useEffect(() => {
    const loadWorklets = async () => {
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }
        const audioContext = audioContextRef.current;
        await Promise.all([
          audioContext.audioWorklet.addModule("/input-audio-processor.js"),
          audioContext.audioWorklet.addModule("/output-audio-processor.js"),
        ]);
        setWorkletsLoaded(true);
      } catch (err) {
        console.error("Error loading worklets:", err);
      }
    };
    loadWorklets();
  }, []);

  useEffect(() => {
    if (!ws && !isConnectingRef.current && selectedCompanyId) {
      isConnectingRef.current = true;

      // Create protocols array with sessionId and companyId (if available)
      // const protocols = [sessionId];
      const protocols = [];
      if (selectedCompanyId) {
        protocols.push(selectedCompanyId);
        protocols.push(localUser?.id || "");
      }

      const baseUrl = import.meta.env.PROD
        ? import.meta.env.VITE_PROD_API_BASE_URL
        : import.meta.env.VITE_DEV_API_BASE_URL;
      const wsUrl = new URL(baseUrl || "");
      wsUrl.protocol = import.meta.env.PROD ? "wss:" : "ws:";
      wsUrl.pathname = "/fews";

      const feWS = new WebSocket(wsUrl.toString(), protocols);

      feWS.onopen = () => {
        console.log("Connected to websocket");
        setWsConnected(true);
        // feWS.send(JSON.stringify({ type: "audio", status: "mic_opened" }));
        isConnectingRef.current = false;
      };

      feWS.onmessage = (event) => {
        const data = JSON.parse(event.data);
        // const excludedEvents = [
        //   "audio_chunk",
        //   "response.audio_transcript.delta",
        //   "response.audio_transcript.done",
        //   "response.content_part.done",
        //   "session.updated",
        //   "response.output_item.added",
        //   "response.content_part.added",
        //   "conversation.item.input_audio_transcription.delta",
        //   "conversation.item.input_audio_transcription.completed",
        //   "conversation.item.created",
        //   "rate_limits.updated",
        //   "audio_stream_end",
        //   "rate_limits.updated",
        //   "response.function_call_arguments.delta",
        //   "input_audio_buffer.committed",
        //   "response.done",
        // ];
        // if (!excludedEvents.includes(data.type)) {
        //   console.log("Received logs or audio event:", data);
        // }

        switch (data.type) {
          case "audio_chunk":
            if (outputProcessorNodeRef.current) {
              const float32Array = base64ToFloat32Array(data.data);
              outputProcessorNodeRef.current.port.postMessage({
                pcmData: float32Array,
              });
            }
            break;
          // case "audio_stream_end":
          //   console.log("Audio stream ended");
          //   break;

          case "input_audio_buffer.speech_started":
            console.log("New speech started");
            if (outputProcessorNodeRef.current) {
              outputProcessorNodeRef.current.port.postMessage({
                emptyBuffer: true,
              });
            }
            handleRealtimeEvent(data, setItems);
            break;

          default:
            handleRealtimeEvent(data, setItems);
            break;
        }
      };

      feWS.onclose = () => {
        console.log("Websocket disconnected");
        setWs(null);
        setWsConnected(false);
        setMicActive(false);
        isConnectingRef.current = false;
      };

      feWS.onerror = () => {
        console.error("WebSocket error occurred");
        isConnectingRef.current = false;
      };

      setWs(feWS);
    }
  }, [ws, selectedCompanyId]);

  // Handle mic start/stop
  const handleMicToggle = async () => {
    if (wsConnected && !micActive) {
      try {
        // Check if worklets are loaded
        if (!workletsLoaded || !audioContextRef.current) {
          console.error("Worklets not loaded yet. Please wait...");
          return;
        }

        const audioContext = audioContextRef.current;

        // Resume context if needed
        if (audioContext.state === "suspended") {
          await audioContext.resume();
        }

        // Get microphone stream with explicit constraints and fallback
        // Enable echo cancellation, noise suppression, and auto gain control
        // to prevent feedback when mic and speaker are close (e.g., laptops)
        // Works with headphones too - echo cancellation is a no-op when no echo exists
        let stream: MediaStream | null = null;
        const audioConstraints = {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        };

        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: selectedAudioInput
              ? { deviceId: { exact: selectedAudioInput }, ...audioConstraints }
              : audioConstraints,
          });
        } catch (e: any) {
          if (e && e.name === "OverconstrainedError") {
            // Retry with default device and relaxed constraints
            console.warn(
              "Audio constraints overconstrained, falling back to defaults"
            );
            try {
              stream = await navigator.mediaDevices.getUserMedia({
                audio: audioConstraints,
              });
            } catch (fallbackError: any) {
              // Final fallback: try with minimal constraints
              console.warn(
                "Echo cancellation may not be available, trying basic audio"
              );
              stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
              });
            }
          } else {
            throw e;
          }
        }
        audioStreamRef.current = stream;

        // Verify echo cancellation is enabled and warn if not
        const trackSettings = stream.getAudioTracks()[0]?.getSettings();
        if (trackSettings) {
          const ecEnabled = trackSettings.echoCancellation;
          if (ecEnabled === false) {
            console.warn(
              "Echo cancellation not available on this device. " +
                "Use headphones to prevent audio feedback on laptops."
            );
          } else {
            console.log("Audio settings:", {
              echoCancellation: trackSettings.echoCancellation,
              noiseSuppression: trackSettings.noiseSuppression,
              autoGainControl: trackSettings.autoGainControl,
            });
          }
        }

        // Create and connect audio nodes
        const microphoneSource = audioContext.createMediaStreamSource(stream);
        const inputAudioProcessorNode = new AudioWorkletNode(
          audioContext,
          "input-audio-processor"
        );
        audioWorkletNodeRef.current = inputAudioProcessorNode;

        // Create output audio processor node
        const outputAudioProcessorNode = new AudioWorkletNode(
          audioContext,
          "output-audio-processor"
        );
        outputProcessorNodeRef.current = outputAudioProcessorNode;

        // Create and configure analyzer node
        const analyserNode = audioContext.createAnalyser();
        analyserNode.fftSize = 2048;
        analyserNode.smoothingTimeConstant = 0.3; // Lower for faster response
        analyserNode.minDecibels = -90; // More sensitive to quiet sounds
        analyserNode.maxDecibels = -10; // Provides a good dynamic range
        analyserNodeRef.current = analyserNode;

        // Set up message handler for input processor
        inputAudioProcessorNode.port.onmessage = (event) => {
          if (
            ws?.readyState === WebSocket.OPEN &&
            event.data.type === "buffer_ready"
          ) {
            ws?.send(event.data.audioData);
          }
        };

        // Connect audio graph:
        // Input path: microphone -> analyzer -> input processor (sends via WebSocket)
        // Output path: output processor -> destination (plays received audio)
        // Note: Input processor doesn't output audio - it only sends data via port
        microphoneSource.connect(analyserNode);
        analyserNode.connect(inputAudioProcessorNode);
        // Keep input processor in graph by connecting to a silent destination
        // This is required for AudioWorklet to continue processing
        const silentGain = audioContext.createGain();
        silentGain.gain.value = 0;
        inputAudioProcessorNode.connect(silentGain);
        silentGain.connect(audioContext.destination);
        // Output audio from AI responses
        outputAudioProcessorNode.connect(audioContext.destination);

        // Update device list in the background after mic is active
        setMicActive(true);
        ws?.send(JSON.stringify({ type: "audio", status: "mic_opened" }));
        // Refresh devices so labels populate across browsers after activation
        await refreshDevices();
      } catch (err) {
        console.error(
          "Microphone access error:",
          err,
          "Browser:",
          navigator.userAgent
        );
        alert("Could not access microphone: " + err);
      }
    } else {
      // Stop mic
      audioStreamRef.current?.getTracks().forEach((t) => t.stop());

      // Clean up audio nodes
      [audioWorkletNodeRef, analyserNodeRef, outputProcessorNodeRef].forEach(
        (ref) => {
          if (ref.current) {
            ref.current.disconnect();
            if ("port" in ref.current) {
              ref.current.port.close();
            }
            ref.current = null;
          }
        }
      );

      // Keep audio context alive since worklets are loaded
      // Just suspend it to save resources
      if (
        audioContextRef.current &&
        audioContextRef.current.state === "running"
      ) {
        await audioContextRef.current.suspend();
      }

      ws?.send(JSON.stringify({ type: "audio", status: "mic_closed" }));
      setMicActive(false);
    }
  };
  return (
    <>
      <div className=" flex flex-col">
        <div className="flex-grow overflow-hidden flex flex-col">
          <div className="flex flex-col gap-4 h-full overflow-hidden">
            <PlaygroundPanel
              micActive={micActive}
              workletsLoaded={workletsLoaded}
              handleMicToggle={handleMicToggle}
              selectedAudioInput={selectedAudioInput}
              setSelectedAudioInput={setSelectedAudioInput}
              audioInputDevices={audioInputDevices}
              audioDevicesLoading={audioDevicesLoading}
              audioDevicesError={audioDevicesError}
              hasAudioPermission={hasAudioPermission}
              onRefreshDevices={refreshDevices}
              onRequestPermission={requestPermission}
              analyserNodeRef={analyserNodeRef as RefObject<AnalyserNode>}
              wsConnected={wsConnected}
              onCompanyChange={(config) => {
                // console.log("Company changed:", config);
                // Close current WebSocket to trigger reconnection with new company ID
                if (ws && config.selectedCompanyId !== selectedCompanyId) {
                  console.log("Closing WebSocket for company change");
                  ws.close();
                }
                setSelectedCompanyId(config.selectedCompanyId);
              }}
              handleGoToCompany={(companyId) => {
                if (ws) {
                  ws.close();
                }
                navigate(`/admin/companies/${companyId}`);
              }}
              companies={companies}
              companiesLoading={companiesLoading}
              selectedCompanyId={selectedCompanyId}
            />
            <PlaygroundTranscript items={items} />
          </div>
        </div>
      </div>
    </>
  );
}
